import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface ActivityLog {
  id: string
  type: string
  description: string
  user: string
  timestamp: string
  metadata: Record<string, unknown>
}

interface AuditLog {
  id: string
  event_type: string
  description: string
  user: string
  severity: string
  timestamp: string
  ip_address: string | null
  user_agent: string | null
  metadata: Record<string, unknown>
}

interface ActivityType {
  id: string
  name: string
  icon: string
  category: string
}

interface AuditEventType {
  id: string
  name: string
  severity: string
}

const API_BASE = import.meta.env.VITE_API_URL || 'http://192.168.0.98:8090/api'

export default function AuditTrail() {
  const [activeTab, setActiveTab] = useState<'activities' | 'audit' | 'search'>('activities')
  const [activities, setActivities] = useState<ActivityLog[]>([])
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([])
  const [activityTypes, setActivityTypes] = useState<ActivityType[]>([])
  const [auditEventTypes, setAuditEventTypes] = useState<AuditEventType[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<(ActivityLog | AuditLog)[]>([])
  const [searchLogType, setSearchLogType] = useState<'all' | 'activity' | 'audit'>('all')

  // Filters
  const [activityUserFilter, setActivityUserFilter] = useState('')
  const [activityTypeFilter, setActivityTypeFilter] = useState('')
  const [auditUserFilter, setAuditUserFilter] = useState('')
  const [auditEventTypeFilter, setAuditEventTypeFilter] = useState('')
  const [auditSeverityFilter, setAuditSeverityFilter] = useState('')

  // Summary data
  const [activitySummary, setActivitySummary] = useState<{
    total_activities: number
    unique_users: number
    by_type: Record<string, number>
  } | null>(null)
  const [auditSummary, setAuditSummary] = useState<{
    total_events: number
    critical_count: number
    warning_count: number
    by_severity: Record<string, number>
  } | null>(null)

  useEffect(() => {
    fetchActivityTypes()
    fetchAuditEventTypes()
    fetchActivities()
    fetchAuditLogs()
    fetchActivitySummary()
    fetchAuditSummary()
  }, [])

  useEffect(() => {
    fetchActivities()
  }, [activityUserFilter, activityTypeFilter])

  useEffect(() => {
    fetchAuditLogs()
  }, [auditUserFilter, auditEventTypeFilter, auditSeverityFilter])

  const fetchActivityTypes = async () => {
    try {
      const response = await fetch(`${API_BASE}/audit-trail/activity-types`)
      if (response.ok) {
        const data = await response.json()
        setActivityTypes(data)
      }
    } catch (error) {
      console.error('Failed to fetch activity types:', error)
    }
  }

  const fetchAuditEventTypes = async () => {
    try {
      const response = await fetch(`${API_BASE}/audit-trail/audit-event-types`)
      if (response.ok) {
        const data = await response.json()
        setAuditEventTypes(data)
      }
    } catch (error) {
      console.error('Failed to fetch audit event types:', error)
    }
  }

  const fetchActivities = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (activityUserFilter) params.append('user', activityUserFilter)
      if (activityTypeFilter) params.append('activity_type', activityTypeFilter)
      params.append('limit', '50')

      const response = await fetch(`${API_BASE}/audit-trail/activities?${params}`)
      if (response.ok) {
        const data = await response.json()
        setActivities(data.activities || [])
      }
    } catch (error) {
      console.error('Failed to fetch activities:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchAuditLogs = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (auditUserFilter) params.append('user', auditUserFilter)
      if (auditEventTypeFilter) params.append('event_type', auditEventTypeFilter)
      if (auditSeverityFilter) params.append('severity', auditSeverityFilter)
      params.append('limit', '50')

      const response = await fetch(`${API_BASE}/audit-trail/audit-logs?${params}`)
      if (response.ok) {
        const data = await response.json()
        setAuditLogs(data.audit_logs || [])
      }
    } catch (error) {
      console.error('Failed to fetch audit logs:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchActivitySummary = async () => {
    try {
      const response = await fetch(`${API_BASE}/audit-trail/activities/summary?period_hours=24`)
      if (response.ok) {
        const data = await response.json()
        setActivitySummary(data)
      }
    } catch (error) {
      console.error('Failed to fetch activity summary:', error)
    }
  }

  const fetchAuditSummary = async () => {
    try {
      const response = await fetch(`${API_BASE}/audit-trail/audit-logs/summary?period_hours=24`)
      if (response.ok) {
        const data = await response.json()
        setAuditSummary(data)
      }
    } catch (error) {
      console.error('Failed to fetch audit summary:', error)
    }
  }

  const handleSearch = async () => {
    if (!searchQuery.trim()) return

    setLoading(true)
    try {
      const params = new URLSearchParams({
        query: searchQuery,
        log_type: searchLogType,
        limit: '50'
      })

      const response = await fetch(`${API_BASE}/audit-trail/search?${params}`)
      if (response.ok) {
        const data = await response.json()
        setSearchResults(data.results || [])
      }
    } catch (error) {
      console.error('Failed to search logs:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    return date.toLocaleDateString()
  }

  const getActivityIcon = (type: string) => {
    const icons: Record<string, string> = {
      login: '🔑',
      logout: '🚪',
      view: '👁️',
      search: '🔍',
      query: '🤖',
      export: '📥',
      report: '📊',
      settings: '⚙️',
      create: '➕',
      update: '✏️',
      delete: '🗑️',
      api_call: '🔌',
      error: '❌'
    }
    return icons[type] || '📋'
  }

  const getSeverityColor = (severity: string) => {
    const colors: Record<string, string> = {
      info: '#3b82f6',
      warning: '#f59e0b',
      error: '#ef4444',
      critical: '#dc2626'
    }
    return colors[severity] || '#6b7280'
  }

  const getSeverityBgColor = (severity: string) => {
    const colors: Record<string, string> = {
      info: 'rgba(59, 130, 246, 0.1)',
      warning: 'rgba(245, 158, 11, 0.1)',
      error: 'rgba(239, 68, 68, 0.1)',
      critical: 'rgba(220, 38, 38, 0.2)'
    }
    return colors[severity] || 'rgba(107, 114, 128, 0.1)'
  }

  const uniqueUsers = Array.from(new Set([
    ...activities.map(a => a.user),
    ...auditLogs.map(a => a.user)
  ])).filter(Boolean)

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#F9FAFB' }}>
      <Header />
      <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#1e293b', marginBottom: '8px' }}>
            Audit Trail & Activity Viewer
          </h1>
          <p style={{ color: '#64748b', fontSize: '14px' }}>
            Monitor user activities, system events, and security audit logs
          </p>
        </div>

        {/* Summary Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px',
          marginBottom: '24px'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
            borderRadius: '12px',
            padding: '20px',
            color: 'white'
          }}>
            <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '4px' }}>Activities (24h)</div>
            <div style={{ fontSize: '32px', fontWeight: '700' }}>
              {activitySummary?.total_activities || 0}
            </div>
            <div style={{ fontSize: '12px', opacity: 0.8, marginTop: '4px' }}>
              {activitySummary?.unique_users || 0} unique users
            </div>
          </div>

          <div style={{
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            borderRadius: '12px',
            padding: '20px',
            color: 'white'
          }}>
            <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '4px' }}>Audit Events (24h)</div>
            <div style={{ fontSize: '32px', fontWeight: '700' }}>
              {auditSummary?.total_events || 0}
            </div>
            <div style={{ fontSize: '12px', opacity: 0.8, marginTop: '4px' }}>
              All severity levels
            </div>
          </div>

          <div style={{
            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
            borderRadius: '12px',
            padding: '20px',
            color: 'white'
          }}>
            <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '4px' }}>Warnings</div>
            <div style={{ fontSize: '32px', fontWeight: '700' }}>
              {auditSummary?.warning_count || 0}
            </div>
            <div style={{ fontSize: '12px', opacity: 0.8, marginTop: '4px' }}>
              Requires attention
            </div>
          </div>

          <div style={{
            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            borderRadius: '12px',
            padding: '20px',
            color: 'white'
          }}>
            <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '4px' }}>Critical</div>
            <div style={{ fontSize: '32px', fontWeight: '700' }}>
              {auditSummary?.critical_count || 0}
            </div>
            <div style={{ fontSize: '12px', opacity: 0.8, marginTop: '4px' }}>
              Immediate action needed
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          gap: '4px',
          marginBottom: '24px',
          background: '#f1f5f9',
          padding: '4px',
          borderRadius: '10px',
          width: 'fit-content'
        }}>
          {[
            { id: 'activities', label: 'Activity Logs', icon: '📋' },
            { id: 'audit', label: 'Audit Events', icon: '🔐' },
            { id: 'search', label: 'Search', icon: '🔍' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              style={{
                padding: '10px 20px',
                borderRadius: '8px',
                border: 'none',
                background: activeTab === tab.id ? 'white' : 'transparent',
                color: activeTab === tab.id ? '#1e293b' : '#64748b',
                fontWeight: activeTab === tab.id ? '600' : '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: activeTab === tab.id ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                transition: 'all 0.2s'
              }}
            >
              <span>{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Activity Logs Tab */}
        {activeTab === 'activities' && (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
            overflow: 'hidden'
          }}>
            {/* Filters */}
            <div style={{
              padding: '16px 20px',
              borderBottom: '1px solid #e2e8f0',
              display: 'flex',
              gap: '12px',
              flexWrap: 'wrap',
              alignItems: 'center'
            }}>
              <select
                value={activityUserFilter}
                onChange={(e) => setActivityUserFilter(e.target.value)}
                style={{
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  fontSize: '14px',
                  minWidth: '180px'
                }}
              >
                <option value="">All Users</option>
                {uniqueUsers.map(user => (
                  <option key={user} value={user}>{user}</option>
                ))}
              </select>

              <select
                value={activityTypeFilter}
                onChange={(e) => setActivityTypeFilter(e.target.value)}
                style={{
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  fontSize: '14px',
                  minWidth: '150px'
                }}
              >
                <option value="">All Types</option>
                {activityTypes.map(type => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
              </select>

              <button
                onClick={() => {
                  setActivityUserFilter('')
                  setActivityTypeFilter('')
                }}
                style={{
                  padding: '8px 16px',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  background: 'white',
                  color: '#64748b',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                Clear Filters
              </button>
            </div>

            {/* Activity List */}
            <div style={{ maxHeight: '600px', overflow: 'auto' }}>
              {loading ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                  Loading activities...
                </div>
              ) : activities.length === 0 ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                  No activities found
                </div>
              ) : (
                activities.map(activity => (
                  <div
                    key={activity.id}
                    style={{
                      padding: '16px 20px',
                      borderBottom: '1px solid #f1f5f9',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '16px',
                      transition: 'background 0.2s',
                      cursor: 'pointer'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.background = '#f8fafc'}
                    onMouseOut={(e) => e.currentTarget.style.background = 'white'}
                  >
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '10px',
                      background: '#f1f5f9',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '18px',
                      flexShrink: 0
                    }}>
                      {getActivityIcon(activity.type)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontWeight: '500',
                        color: '#1e293b',
                        marginBottom: '4px'
                      }}>
                        {activity.description}
                      </div>
                      <div style={{
                        fontSize: '13px',
                        color: '#64748b',
                        display: 'flex',
                        gap: '12px',
                        flexWrap: 'wrap'
                      }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <span style={{ opacity: 0.7 }}>User:</span> {activity.user}
                        </span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <span style={{ opacity: 0.7 }}>Type:</span>
                          <span style={{
                            background: '#e0e7ff',
                            color: '#4f46e5',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: '500'
                          }}>
                            {activity.type}
                          </span>
                        </span>
                      </div>
                    </div>
                    <div style={{
                      fontSize: '13px',
                      color: '#94a3b8',
                      whiteSpace: 'nowrap'
                    }}>
                      {formatTimestamp(activity.timestamp)}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Audit Events Tab */}
        {activeTab === 'audit' && (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
            overflow: 'hidden'
          }}>
            {/* Filters */}
            <div style={{
              padding: '16px 20px',
              borderBottom: '1px solid #e2e8f0',
              display: 'flex',
              gap: '12px',
              flexWrap: 'wrap',
              alignItems: 'center'
            }}>
              <select
                value={auditUserFilter}
                onChange={(e) => setAuditUserFilter(e.target.value)}
                style={{
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  fontSize: '14px',
                  minWidth: '180px'
                }}
              >
                <option value="">All Users</option>
                {uniqueUsers.map(user => (
                  <option key={user} value={user}>{user}</option>
                ))}
              </select>

              <select
                value={auditEventTypeFilter}
                onChange={(e) => setAuditEventTypeFilter(e.target.value)}
                style={{
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  fontSize: '14px',
                  minWidth: '150px'
                }}
              >
                <option value="">All Event Types</option>
                {auditEventTypes.map(type => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
              </select>

              <select
                value={auditSeverityFilter}
                onChange={(e) => setAuditSeverityFilter(e.target.value)}
                style={{
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  fontSize: '14px',
                  minWidth: '120px'
                }}
              >
                <option value="">All Severities</option>
                <option value="info">Info</option>
                <option value="warning">Warning</option>
                <option value="error">Error</option>
                <option value="critical">Critical</option>
              </select>

              <button
                onClick={() => {
                  setAuditUserFilter('')
                  setAuditEventTypeFilter('')
                  setAuditSeverityFilter('')
                }}
                style={{
                  padding: '8px 16px',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  background: 'white',
                  color: '#64748b',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                Clear Filters
              </button>
            </div>

            {/* Audit Log List */}
            <div style={{ maxHeight: '600px', overflow: 'auto' }}>
              {loading ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                  Loading audit logs...
                </div>
              ) : auditLogs.length === 0 ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                  No audit logs found
                </div>
              ) : (
                auditLogs.map(audit => (
                  <div
                    key={audit.id}
                    style={{
                      padding: '16px 20px',
                      borderBottom: '1px solid #f1f5f9',
                      borderLeft: `4px solid ${getSeverityColor(audit.severity)}`,
                      background: getSeverityBgColor(audit.severity),
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '16px',
                      transition: 'all 0.2s',
                      cursor: 'pointer'
                    }}
                  >
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '10px',
                      background: getSeverityColor(audit.severity),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '16px',
                      fontWeight: '600',
                      flexShrink: 0
                    }}>
                      {audit.severity === 'critical' ? '!!' :
                       audit.severity === 'error' ? '!' :
                       audit.severity === 'warning' ? '⚠' : 'i'}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontWeight: '500',
                        color: '#1e293b',
                        marginBottom: '4px'
                      }}>
                        {audit.description}
                      </div>
                      <div style={{
                        fontSize: '13px',
                        color: '#64748b',
                        display: 'flex',
                        gap: '12px',
                        flexWrap: 'wrap'
                      }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <span style={{ opacity: 0.7 }}>User:</span> {audit.user}
                        </span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <span style={{ opacity: 0.7 }}>Event:</span>
                          <span style={{
                            background: 'rgba(0,0,0,0.05)',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: '500'
                          }}>
                            {audit.event_type}
                          </span>
                        </span>
                        <span style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '600',
                          textTransform: 'uppercase',
                          background: getSeverityColor(audit.severity),
                          color: 'white'
                        }}>
                          {audit.severity}
                        </span>
                      </div>
                      {audit.ip_address && (
                        <div style={{
                          fontSize: '12px',
                          color: '#94a3b8',
                          marginTop: '4px'
                        }}>
                          IP: {audit.ip_address}
                        </div>
                      )}
                    </div>
                    <div style={{
                      fontSize: '13px',
                      color: '#94a3b8',
                      whiteSpace: 'nowrap'
                    }}>
                      {formatTimestamp(audit.timestamp)}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Search Tab */}
        {activeTab === 'search' && (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
            overflow: 'hidden'
          }}>
            {/* Search Form */}
            <div style={{
              padding: '20px',
              borderBottom: '1px solid #e2e8f0'
            }}>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder="Search activities and audit logs..."
                  style={{
                    flex: 1,
                    minWidth: '250px',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    fontSize: '14px',
                    outline: 'none'
                  }}
                />
                <select
                  value={searchLogType}
                  onChange={(e) => setSearchLogType(e.target.value as typeof searchLogType)}
                  style={{
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    fontSize: '14px'
                  }}
                >
                  <option value="all">All Logs</option>
                  <option value="activity">Activity Only</option>
                  <option value="audit">Audit Only</option>
                </select>
                <button
                  onClick={handleSearch}
                  style={{
                    padding: '12px 24px',
                    borderRadius: '8px',
                    border: 'none',
                    background: '#3b82f6',
                    color: 'white',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Search
                </button>
              </div>
            </div>

            {/* Search Results */}
            <div style={{ maxHeight: '600px', overflow: 'auto' }}>
              {searchResults.length === 0 ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                  {searchQuery ? 'No results found' : 'Enter a search query to find logs'}
                </div>
              ) : (
                searchResults.map((result, index) => {
                  const isAudit = 'event_type' in result
                  return (
                    <div
                      key={`${result.id}-${index}`}
                      style={{
                        padding: '16px 20px',
                        borderBottom: '1px solid #f1f5f9',
                        borderLeft: isAudit ? `4px solid ${getSeverityColor((result as AuditLog).severity)}` : '4px solid #3b82f6',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '16px'
                      }}
                    >
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '10px',
                        background: isAudit ? getSeverityColor((result as AuditLog).severity) : '#f1f5f9',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: isAudit ? '16px' : '18px',
                        color: isAudit ? 'white' : 'inherit',
                        fontWeight: isAudit ? '600' : '400',
                        flexShrink: 0
                      }}>
                        {isAudit ?
                          ((result as AuditLog).severity === 'critical' ? '!!' : 'i') :
                          getActivityIcon((result as ActivityLog).type)
                        }
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          marginBottom: '4px'
                        }}>
                          <span style={{
                            fontSize: '11px',
                            fontWeight: '600',
                            textTransform: 'uppercase',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            background: isAudit ? '#fef3c7' : '#dbeafe',
                            color: isAudit ? '#92400e' : '#1e40af'
                          }}>
                            {isAudit ? 'Audit' : 'Activity'}
                          </span>
                          <span style={{ fontWeight: '500', color: '#1e293b' }}>
                            {result.description}
                          </span>
                        </div>
                        <div style={{
                          fontSize: '13px',
                          color: '#64748b'
                        }}>
                          User: {result.user}
                        </div>
                      </div>
                      <div style={{
                        fontSize: '13px',
                        color: '#94a3b8',
                        whiteSpace: 'nowrap'
                      }}>
                        {formatTimestamp(result.timestamp)}
                      </div>
                    </div>
                  )
                })
              )}
            </div>
          </div>
        )}

        {/* Activity Type Distribution */}
        {activeTab === 'activities' && activitySummary && Object.keys(activitySummary.by_type).length > 0 && (
          <div style={{
            marginTop: '24px',
            background: 'white',
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
            padding: '20px'
          }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: '#1e293b' }}>
              Activity Distribution (Last 24 Hours)
            </h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
              {Object.entries(activitySummary.by_type).map(([type, count]) => (
                <div
                  key={type}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 16px',
                    background: '#f8fafc',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0'
                  }}
                >
                  <span style={{ fontSize: '18px' }}>{getActivityIcon(type)}</span>
                  <span style={{ fontWeight: '500', color: '#1e293b' }}>{type}</span>
                  <span style={{
                    background: '#3b82f6',
                    color: 'white',
                    padding: '2px 8px',
                    borderRadius: '10px',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}>
                    {count}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Severity Distribution */}
        {activeTab === 'audit' && auditSummary && (
          <div style={{
            marginTop: '24px',
            background: 'white',
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
            padding: '20px'
          }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: '#1e293b' }}>
              Severity Distribution (Last 24 Hours)
            </h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
              {Object.entries(auditSummary.by_severity).map(([severity, count]) => (
                <div
                  key={severity}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 16px',
                    background: getSeverityBgColor(severity),
                    borderRadius: '8px',
                    border: `1px solid ${getSeverityColor(severity)}30`
                  }}
                >
                  <span style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    background: getSeverityColor(severity)
                  }} />
                  <span style={{
                    fontWeight: '500',
                    color: '#1e293b',
                    textTransform: 'capitalize'
                  }}>
                    {severity}
                  </span>
                  <span style={{
                    background: getSeverityColor(severity),
                    color: 'white',
                    padding: '2px 8px',
                    borderRadius: '10px',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}>
                    {count}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
